/**
 * 提醒偏好面板**中文文案唯一源**（N 域 · 端侧）
 *
 * 依据：原型 `09-提醒中心_原型.html`（三档总开关 / 五类开关 / 免打扰时段的**原文文案**）·
 * N 域 PRD **§5.2 N2**（提醒偏好：策略 H 域 / 存储与 UI N 域）· **§7.2 `AC-N2.20`–`N2.24`** ·
 * H 域 PRD **§5.3.1**（三档释义）· **§5.3.2**（五类）· **§5.3.3**（免打扰）。
 *
 * ## 🔴 端侧零口径
 *
 * 选项集合与默认值**不在本文件**：默认值由服务端下发（无行时给表默认值），
 * 三档取值来自共享层枚举 `ReminderLevel`，五类来自 `ReminderType`。
 * 本文件只做**选词**（把枚举值翻成中文 + 一句解释）。
 */

import { ReminderLevel, ReminderType } from '@jiaying/shared';

// ─────────────────────────── 面板 ───────────────────────────

export const PANEL_TITLE = '提醒偏好';
export const PANEL_NOTE = '这里决定「什么时候打扰你」。站内提醒中心**始终可用**，不受这里的开关影响。';
/** 🔴 作用域说明（`DR-020` 层级标注铁律：用户级偏好换设备保持、且**不影响家人**） */
export const PANEL_SCOPE_NOTE = '这是你自己的设置，家人不受影响；换设备登录后保持。';

export const LEVEL_TITLE = '打扰程度';
export const TYPE_TITLE = '按类型开关';
export const DND_TITLE = '免打扰时段';

export const SAVE_LABEL = '保存';
export const SAVED_LABEL = '已保存';
export const SAVED_STYLE_LABEL = '保存失败：';
export const NO_CHANGE_LABEL = '（没有改动）';

/** 🔴 「免打扰按账本时区」必须写出来 —— 否则用户会以为它按手机时区 */
export function dndTimezoneNote(timezone: string): string {
  return `按账本时区（${timezone}）计算。时段内**不推送任何通知**（含关键提醒），但提醒仍会留在站内。`;
}

export const DND_DISABLED_NOTE = '已关闭：任何时段都可能推送（仍受上面的类型与档位限制）。';

// ─────────────────────────── 三档（H 域 §5.3.1） ───────────────────────────

/**
 * 🔴 三档文案**逐字取自原型**（`09-提醒中心_原型.html` 的 `.level-opt`）：
 * 原型明写「**常规（推荐）**」，且声明「我们不做『默认全开』再让你一条条关掉」。
 */
export const LEVEL_OPTIONS: readonly {
  id: ReminderLevel;
  label: string;
  desc: string;
}[] = [
  {
    id: ReminderLevel.KeyOnly,
    label: '只推关键',
    desc: '还款日、账单到期、超支 —— 别的都不打扰你',
  },
  {
    id: ReminderLevel.Normal,
    label: '常规',
    desc: '关键 + 预算预警 + 目标进度 + 每周检视',
  },
  {
    id: ReminderLevel.All,
    label: '全部',
    desc: '以上全部 + 每日记账提醒 + 新账单导入提示',
  },
];

/** 🔴 默认档「常规」在 UI 上要说出来（`AC-N2.20`：默认「常规」）—— 不给"默认全开" */
export const RECOMMENDED_TAG = '推荐（默认）';

export function levelLabelOf(level: ReminderLevel): string {
  return LEVEL_OPTIONS.find((o) => o.id === level)?.label ?? level;
}

// ─────────────────────────── 五类（H 域 §5.1.1 / §5.3.2） ───────────────────────────

/**
 * 🔴 五类的**名称 + 那句解释**逐字取自原型（「提前 5 天 + 当天」「账单日出账后次日」
 * 「月度 80% / 100%；季年事件按时间进度偏离」「里程碑与节奏偏离」「查漏补记 + 每周检视」）——
 * 这些是**用户唯一能看到提前量的地方**，不写就等于让人不知道自己在关掉什么。
 */
export const TYPE_ROWS: readonly {
  id: ReminderType;
  label: string;
  desc: string;
}[] = [
  { id: ReminderType.Repayment, label: '还款日提醒', desc: '提前 5 天 + 当天；逾期后每天提醒' },
  { id: ReminderType.Billing, label: '信用卡账单日', desc: '账单日出账后次日告知本期应还' },
  {
    id: ReminderType.Budget,
    label: '预算预警',
    desc: '月度 80% / 100%；季年事件按时间进度偏离',
  },
  { id: ReminderType.Goal, label: '目标进度', desc: '里程碑达成 · 节奏偏离' },
  { id: ReminderType.Habit, label: '记账习惯', desc: '查漏补记 + 每周检视' },
];
